import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Tick = ({ ok, testId }) => ok
  ? <Check data-testid={testId} data-ok="true" className="w-4 h-4 text-[#2D6A4F]" strokeWidth={2} />
  : <X data-testid={testId} data-ok="false" className="w-4 h-4 text-[#B42318]" strokeWidth={2} />;

const COLS = "min-w-[620px] grid grid-cols-[1fr_60px_60px_60px_64px_28px]";

const Row = ({ r, onFix, fixing }) => (
  <div data-testid={`sync-row-${r.kind}-${r.id}`} className={`${COLS} items-center gap-2 px-4 py-2.5 border-b border-[#E2D9CC] last:border-0 text-sm`}>
    <span className="truncate"><span className="font-display text-[#C59B27] mr-2">{r.kind === "pack" ? "Pack" : r.issue_number}</span>{r.name}
      {!r.edition_ok && <span className="block text-[11px] text-[#B45309]">Shop shows Ed. {r.shop_edition}, studio has Ed. {r.edition}</span>}</span>
    <Tick ok={r.in_feed} testId={`sync-feed-${r.id}`} /><Tick ok={r.in_shop} testId={`sync-shop-${r.id}`} /><Tick ok={r.page_ok} testId={`sync-page-${r.id}`} />
    <span>{!r.ok && <button data-testid={`sync-fix-${r.id}`} disabled={fixing} onClick={() => onFix(r)} className="text-xs border border-[#3B1B48] text-[#3B1B48] px-2.5 py-1 hover:bg-[#3B1B48] hover:text-[#FAF7F2] transition-colors disabled:opacity-50">{fixing === r.id ? "…" : "Fix"}</button>}</span>
    <a href={r.page_url} target="_blank" rel="noreferrer" data-testid={`sync-open-${r.id}`} className="text-[#8C827A] hover:text-[#3B1B48]"><ExternalLink className="w-4 h-4" /></a>
  </div>
);

const Summary = ({ d }) => {
  const bad = Object.entries(d.sources || {}).filter(([, s]) => s.status !== 200);
  return (
    <div data-testid="sync-summary" className={`px-4 py-3 text-sm border ${d.all_ok ? "border-[#2D6A4F]/40 bg-[#2D6A4F]/10 text-[#2D6A4F]" : "border-[#B45309]/40 bg-[#B45309]/10 text-[#7C3A06]"}`}>
      {d.all_ok ? `All ${d.total} guides and packs are visible on the shop.` : `${d.ok_count} of ${d.total} fully visible.`}
      {bad.map(([k, s]) => <span key={k} className="block text-xs mt-1">Could not read {s.url} ({s.status ?? s.error})</span>)}
      {d.extra_on_shop?.length > 0 && <span data-testid="sync-extra" className="block text-xs mt-1">On the shop but not published here: {d.extra_on_shop.map((x) => x.name).join(", ")}</span>}
      {d.alert_sent && <span data-testid="sync-last-alert" className="block text-[11px] text-[#8C827A] mt-1">Last alert emailed to {d.alert_sent.to} · {new Date(d.alert_sent.at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>}
      <span className="block text-[11px] text-[#8C827A] mt-1">Checked {new Date(d.at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
    </div>
  );
};

export const ShopSyncCheck = () => {
  const [d, setD] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(null);
  const fix = async (r) => {
    setFixing(r.id);
    try {
      const { data } = await api.post("/store/sync-check/fix", { kind: r.kind, id: r.id });
      setD((prev) => ({ ...data.check, alert_sent: prev?.alert_sent }));
      data.row?.ok ? toast.success(`${r.name} is back on the shop`) : toast.warning(`${r.name} re-published; the shop hasn't picked it up yet. Check again in a few minutes.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setFixing(null); }
  };
  useEffect(() => { api.get("/store/sync-check").then((r) => { setD(r.data.at ? r.data : null); setUrl(r.data.shop_url); }).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true);
    try {
      await api.put("/store/shop-url", { shop_url: url });
      const { data } = await api.post("/store/sync-check");
      setD(data);
      data.all_ok ? toast.success("Everything is visible on the shop") : toast.warning(`${data.total - data.ok_count} item(s) need attention`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="shop-sync-check" className="bg-[#F4EFE6] border border-[#E2D9CC] p-6 space-y-4">
      <h2 className="font-display text-2xl text-[#3B1B48] flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#C59B27]" strokeWidth={1.5} />Shop sync check</h2>
      <p className="text-sm text-[#8C827A]">Confirms every published guide and pack is in this studio's public feed, is listed by your shop, and has a working product page. Runs automatically every day at 1pm Brisbane time and emails your reminder address only if something is out of sync. A weekly pipeline summary (what's built, posted, listed and selling, anything stuck, shop status, sales, AI credit) goes out every Monday at 8am Brisbane time.</p>
      <div className="flex flex-wrap gap-3">
        <Input data-testid="shop-url-input" value={url} onChange={(e) => setUrl(e.target.value)} className="h-10 bg-white rounded-none flex-1 min-w-[220px]" placeholder="https://theexpatstudio.com" />
        <Button data-testid="weekly-summary-test-button" variant="outline" disabled={busy} onClick={async () => { setBusy(true); try { const { data } = await api.post("/store/weekly-summary/test"); toast.success(`Weekly summary sent to ${data.sent_to}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } }} className="rounded-none h-10">Send weekly summary now</Button>
        <Button data-testid="shop-sync-check-button" onClick={run} disabled={busy || !url} className="rounded-none h-10">{busy ? "Checking…" : "Check shop now"}</Button>
      </div>
      {d && <Summary d={d} />}
      {d?.rows?.length > 0 && (
        <div className="border border-[#E2D9CC] bg-white overflow-x-auto" data-testid="sync-results">
          <div className={`${COLS} gap-2 px-4 py-2 bg-[#FAF7F2] text-[10px] uppercase tracking-wider text-[#8C827A] border-b border-[#E2D9CC]`}>
            <span>Guide / pack</span><span>Feed</span><span>Shop</span><span>Page</span><span /><span />
          </div>
          {d.rows.map((r) => <Row key={r.kind + r.id} r={r} onFix={fix} fixing={fixing} />)}
        </div>
      )}
    </div>
  );
};
